import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import { User, ShoppingBag, MapPin, Lock, LogOut, Plus, Edit2, Trash2, Star, MessageSquare } from 'lucide-react';
import { ReviewFormModal } from '../components/ReviewSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const [activeTab, setActiveTab] = useState('account');
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [reviewableProducts, setReviewableProducts] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewItem, setReviewItem] = useState(null);
  const [reviewSuccessMsg, setReviewSuccessMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  
  const [accountForm, setAccountForm] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [addressForm, setAddressForm] = useState({
    title: '',
    full_name: '',
    phone: '',
    address_line: '',
    city: '',
    district: '',
    postal_code: '',
    is_default: false
  });

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('userToken');
    
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setAccountForm({
          first_name: userData.first_name,
          last_name: userData.last_name,
          phone: userData.phone || ''
        });
        setAddresses(userData.addresses || []);
        fetchOrders(token);
      } else {
        localStorage.removeItem('userToken');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('userToken');

    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(accountForm)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        setEditMode(false);
        alert('✅ Bilgileriniz güncellendi!');
      } else {
        alert('❌ Güncelleme başarısız!');
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert('❌ Yeni şifreler eşleşmiyor!');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      alert('❌ Şifre en az 8 karakter olmalı!');
      return;
    }
    if (!/\d/.test(passwordForm.new_password)) {
      alert('❌ Şifre en az bir rakam içermelidir!');
      return;
    }
    if (!/[A-Za-zğüşıöçĞÜŞİÖÇ]/.test(passwordForm.new_password)) {
      alert('❌ Şifre en az bir harf içermelidir!');
      return;
    }

    const token = localStorage.getItem('userToken');

    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          old_password: passwordForm.old_password,
          new_password: passwordForm.new_password
        })
      });

      if (response.ok) {
        alert('✅ Şifreniz değiştirildi!');
        setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
      } else {
        const data = await response.json();
        alert(`❌ ${data.detail || 'Şifre değiştirilemedi'}`);
      }
    } catch (error) {
      console.error('Password change error:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('userToken');

    try {
      const url = editingAddressId 
        ? `${API_URL}/api/auth/addresses/${editingAddressId}`
        : `${API_URL}/api/auth/addresses`;
      
      const method = editingAddressId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addressForm)
      });

      if (response.ok) {
        alert(editingAddressId ? '✅ Adres güncellendi!' : '✅ Adres eklendi!');
        setShowAddressModal(false);
        setEditingAddressId(null);
        setAddressForm({
          title: '',
          full_name: '',
          phone: '',
          address_line: '',
          city: '',
          district: '',
          postal_code: '',
          is_default: false
        });
        checkAuth(); // Refresh data
      } else {
        alert('❌ İşlem başarısız!');
      }
    } catch (error) {
      console.error('Address error:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Bu adresi silmek istediğinize emin misiniz?')) return;

    const token = localStorage.getItem('userToken');

    try {
      const response = await fetch(`${API_URL}/api/auth/addresses/${addressId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ Adres silindi!');
        checkAuth();
      } else {
        alert('❌ Silme başarısız!');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST' });
    } catch (_) { /* ignore */ }
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  // Load reviewable products when the tab is opened
  const loadReviewableProducts = async () => {
    setLoadingReviews(true);
    try {
      const token = localStorage.getItem('userToken');
      const r = await fetch(`${API_URL}/api/user/reviewable-products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) {
        const d = await r.json();
        setReviewableProducts(d.products || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingReviews(false); }
  };

  useEffect(() => {
    if (activeTab === 'reviews') loadReviewableProducts();
  }, [activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header cartCount={getCartCount()} />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
        <Footer />
      </div>
    );
  }

  const tabs = [
    { id: 'account', name: 'Hesap Bilgilerim', icon: User },
    { id: 'orders', name: 'Siparişlerim', icon: ShoppingBag },
    { id: 'reviews', name: 'Yorumlarım', icon: MessageSquare },
    { id: 'addresses', name: 'Adreslerim', icon: MapPin },
    { id: 'password', name: 'Şifre Değiştir', icon: Lock }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Profilim - Cep Premium"
        description="Üye profil sayfası"
      />
      <Header cartCount={getCartCount()} />

      <div className="container mx-auto px-4 py-8">
        {/* Welcome Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Hoş Geldiniz, {user?.first_name} {user?.last_name}
              </h1>
              <p className="text-gray-600 mt-1">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} />
              Çıkış Yap
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Tabs */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4 space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-tab={tab.id}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-red-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{tab.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-md p-6">
              {/* Account Tab */}
              {activeTab === 'account' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Hesap Bilgilerim</h2>
                    <button
                      onClick={() => setEditMode(!editMode)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      {editMode ? 'İptal' : 'Düzenle'}
                    </button>
                  </div>

                  <form onSubmit={handleUpdateAccount} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ad</label>
                        <input
                          type="text"
                          disabled={!editMode}
                          value={accountForm.first_name}
                          onChange={(e) => setAccountForm({...accountForm, first_name: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Soyad</label>
                        <input
                          type="text"
                          disabled={!editMode}
                          value={accountForm.last_name}
                          onChange={(e) => setAccountForm({...accountForm, last_name: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        disabled
                        value={user?.email}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                      <p className="text-sm text-gray-500 mt-1">Email değiştirilemez</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                      <input
                        type="tel"
                        disabled={!editMode}
                        value={accountForm.phone}
                        onChange={(e) => setAccountForm({...accountForm, phone: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                        placeholder="0555 123 4567"
                      />
                    </div>

                    {editMode && (
                      <button
                        type="submit"
                        className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700"
                      >
                        Kaydet
                      </button>
                    )}
                  </form>
                </div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Siparişlerim</h2>
                  {orders.length === 0 ? (
                    <p className="text-gray-600 text-center py-8">Henüz siparişiniz bulunmamaktadır.</p>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order) => (
                        <div key={order.order_id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <p className="font-semibold text-gray-800 text-lg">Sipariş #{order.order_id}</p>
                              <p className="text-sm text-gray-600 mt-1">
                                {new Date(order.created_at).toLocaleDateString('tr-TR', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                              order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                              order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {order.status === 'pending' ? 'Beklemede' :
                               order.status === 'confirmed' ? 'Onaylandı' :
                               order.status === 'shipped' ? 'Kargoda' :
                               order.status === 'delivered' ? 'Teslim Edildi' : 'İptal'}
                            </span>
                          </div>
                          
                          {/* Order Items */}
                          <div className="border-t border-gray-200 pt-4 mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-3">
                              Sipariş Ürünleri ({order.items.length} ürün)
                            </p>
                            <div className="space-y-3">
                              {order.items.map((item, idx) => (
                                <div key={idx} className="flex items-start justify-between py-2 border-b last:border-b-0 border-gray-100">
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-800 text-sm">
                                      {item.product_name}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Adet: {item.quantity} × {item.price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                                    </p>
                                  </div>
                                  <div className="text-right ml-4">
                                    <p className="font-semibold text-red-600 text-sm">
                                      {item.subtotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Total */}
                          <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                            <span className="text-gray-700 font-medium">Toplam Tutar:</span>
                            <span className="text-xl font-bold text-gray-900">
                              {order.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Reviews Tab */}
              {activeTab === 'reviews' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <MessageSquare className="text-red-600" /> Yorumlarım
                  </h2>
                  
                  {reviewSuccessMsg && (
                    <div className="bg-green-50 border border-green-200 text-green-700 rounded p-3 mb-4 text-sm">
                      {reviewSuccessMsg}
                    </div>
                  )}
                  
                  {loadingReviews ? (
                    <p className="text-gray-500">Yükleniyor...</p>
                  ) : reviewableProducts.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <MessageSquare size={48} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500 font-medium">Henüz yorum yapabileceğiniz ürün yok</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Bir sipariş verip teslim aldıktan sonra burada listelenir.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded p-3 mb-2">
                        💡 Teslim edilen ürünleriniz için yorum yazabilirsiniz. Yorumunuz admin onayından sonra ürün sayfasında yayınlanır.
                      </p>
                      {reviewableProducts.map(item => (
                        <div key={`${item.product_id}-${item.order_id}`} className="border rounded-lg p-4 hover:bg-gray-50 flex gap-4 items-center">
                          <img src={item.product_image} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-800 line-clamp-2">{item.product_name}</p>
                            <p className="text-xs text-gray-500 mt-1">Sipariş: {item.order_id}</p>
                          </div>
                          {item.already_reviewed ? (
                            <span className="text-xs bg-gray-100 text-gray-600 px-3 py-2 rounded flex items-center gap-1">
                              <Star size={14} className="fill-yellow-400 text-yellow-400" /> Yorumlandı
                            </span>
                          ) : (
                            <button
                              onClick={() => setReviewItem(item)}
                              data-testid={`write-review-${item.product_id}`}
                              className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-semibold flex items-center gap-1 flex-shrink-0"
                            >
                              <Star size={14} /> Yorum Yaz
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Addresses Tab */}
              {activeTab === 'addresses' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Adreslerim</h2>
                    <button
                      onClick={() => {
                        setShowAddressModal(true);
                        setEditingAddressId(null);
                        setAddressForm({
                          title: '',
                          full_name: '',
                          phone: '',
                          address_line: '',
                          city: '',
                          district: '',
                          postal_code: '',
                          is_default: false
                        });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <Plus size={20} />
                      Yeni Adres Ekle
                    </button>
                  </div>

                  {addresses.length === 0 ? (
                    <p className="text-gray-600 text-center py-8">Kayıtlı adres bulunmamaktadır.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {addresses.map((address) => (
                        <div key={address.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-gray-800">{address.title}</h3>
                            {address.is_default && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                Varsayılan
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 font-medium">{address.full_name}</p>
                          <p className="text-sm text-gray-600 mt-1">{address.phone}</p>
                          <p className="text-sm text-gray-600 mt-2">{address.address_line}</p>
                          <p className="text-sm text-gray-600">{address.district}, {address.city}</p>
                          {address.postal_code && (
                            <p className="text-sm text-gray-600">Posta Kodu: {address.postal_code}</p>
                          )}
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => {
                                setEditingAddressId(address.id);
                                setAddressForm(address);
                                setShowAddressModal(true);
                              }}
                              className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
                            >
                              <Edit2 size={14} />
                              Düzenle
                            </button>
                            <button
                              onClick={() => handleDeleteAddress(address.id)}
                              className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 border border-red-600 rounded hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                              Sil
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Password Tab */}
              {activeTab === 'password' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Şifre Değiştir</h2>
                  <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mevcut Şifre
                      </label>
                      <input
                        type="password"
                        required
                        value={passwordForm.old_password}
                        onChange={(e) => setPasswordForm({...passwordForm, old_password: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Yeni Şifre
                      </label>
                      <input
                        type="password"
                        required
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="En az 6 karakter"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Yeni Şifre Tekrar
                      </label>
                      <input
                        type="password"
                        required
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700"
                    >
                      Şifreyi Değiştir
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-4">
              {editingAddressId ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}
            </h3>
            
            <form onSubmit={handleAddAddress} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adres Başlığı *</label>
                  <input
                    type="text"
                    required
                    value={addressForm.title}
                    onChange={(e) => setAddressForm({...addressForm, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ev, İş, vb."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ad Soyad *</label>
                  <input
                    type="text"
                    required
                    value={addressForm.full_name}
                    onChange={(e) => setAddressForm({...addressForm, full_name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefon *</label>
                <input
                  type="tel"
                  required
                  value={addressForm.phone}
                  onChange={(e) => setAddressForm({...addressForm, phone: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adres *</label>
                <textarea
                  required
                  value={addressForm.address_line}
                  onChange={(e) => setAddressForm({...addressForm, address_line: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">İl *</label>
                  <input
                    type="text"
                    required
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">İlçe *</label>
                  <input
                    type="text"
                    required
                    value={addressForm.district}
                    onChange={(e) => setAddressForm({...addressForm, district: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Posta Kodu</label>
                  <input
                    type="text"
                    value={addressForm.postal_code}
                    onChange={(e) => setAddressForm({...addressForm, postal_code: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={addressForm.is_default}
                    onChange={(e) => setAddressForm({...addressForm, is_default: e.target.checked})}
                    className="rounded border-gray-300 text-red-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Varsayılan adres olarak ayarla</span>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700"
                >
                  {editingAddressId ? 'Güncelle' : 'Kaydet'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddressModal(false);
                    setEditingAddressId(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
      
      {/* Review form modal */}
      {reviewItem && (
        <ReviewFormModal
          item={reviewItem}
          onClose={() => setReviewItem(null)}
          onSubmitted={(msg) => {
            setReviewSuccessMsg(msg);
            loadReviewableProducts();
            setTimeout(() => setReviewSuccessMsg(null), 8000);
          }}
        />
      )}
    </div>
  );
};

export default ProfilePage;
